import React, { useEffect } from 'react';
import type { CSSProperties } from 'react';
import { getStoredLang } from '../../utils/auth';

// The Google Translate script installs these on `window` at runtime.
declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: {
      translate: {
        TranslateElement: new (
          options: { pageLanguage: string; includedLanguages: string; autoDisplay: boolean },
          elementId: string
        ) => void;
      };
    };
  }
}

// Google Translate has no headless mode, so the widget is loaded, pushed
// off-screen and its injected chrome suppressed. All ~60 lines of that hack
// used to sit inline in App.js.

export const setTranslateCookie = (langCode?: string): void => {
  if (!langCode || langCode === 'en') {
    clearTranslateCookie();
    return;
  }
  document.cookie = `googtrans=/en/${langCode}; path=/; domain=${window.location.hostname}`;
};

export const clearTranslateCookie = (): void => {
  document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
};

const HIDDEN_ELEMENT_STYLE: CSSProperties = {
  position: 'fixed', top: '-99999px', left: '-99999px',
  width: '0px', height: '0px', opacity: 0, zIndex: -99999,
  pointerEvents: 'none', overflow: 'hidden'
};

// Google ships obfuscated class names that change; these cover the banner,
// gadget, spinner, tooltip and balloon frames.
const SUPPRESSION_CSS = `
  .goog-te-banner-frame,
  .skiptranslate iframe,
  .goog-te-gadget,
  .goog-te-gadget-simple,
  .goog-te-spinner-pos,
  .goog-te-spinner-animation,
  .goog-logo-link,
  img.goog-te-gadget-icon,
  #goog-gt-tt,
  .goog-te-balloon-frame,
  .VIpgJd-ZVi9od-aZ2wEe-wOHMyf,
  .VIpgJd-ZVi9od-aZ2wEe-wOHMyf-ti6hGc,
  .VIpgJd-ZVi9od-ORHb-OEVmcd,
  div[id^="goog-gt-"] {
    display: none !important;
    opacity: 0 !important;
    visibility: hidden !important;
    pointer-events: none !important;
  }

  /* Google shifts <body> down to make room for its banner. */
  body {
    top: 0 !important;
    margin-top: 0 !important;
    position: static !important;
  }

  /*
   * Google wraps every translated run in its own <font> element and highlights
   * it on hover. Left alone those carry Google's colours, which showed as
   * white text sitting on the dark-mode hover background — unreadable
   * (CHM-108). Colour and background always come from the app's own tokens.
   */
  font,
  .goog-text-highlight,
  .goog-text-highlight * {
    color: inherit !important;
    background: transparent !important;
    background-color: transparent !important;
    box-shadow: none !important;
    text-shadow: none !important;
  }
`;

const GoogleTranslateShim = () => {
  useEffect(() => {
    const lang = getStoredLang();
    if (!lang || lang === 'en') return;
    if (document.getElementById('google-translate-script')) return;

    window.googleTranslateElementInit = () => {
      // eslint-disable-next-line no-new
      new window.google.translate.TranslateElement(
        { pageLanguage: 'en', includedLanguages: lang, autoDisplay: true },
        'google_translate_element'
      );
    };

    const script = document.createElement('script');
    script.id = 'google-translate-script';
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <>
      <div id="google_translate_element" style={HIDDEN_ELEMENT_STYLE} />
      <style>{SUPPRESSION_CSS}</style>
    </>
  );
};

export default GoogleTranslateShim;
